import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { MatDialog } from '@angular/material/dialog';
import { JsonModalDataObject } from '@src/app/model/json-modal-data-object';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatestWith, Observable, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import {
  LocalizedStringForApp,
  LocalizedStringForAttribute,
  LocalizedStringForInvariant,
  LocalizedStringForVariant,
  TranslationStringMap,
  TranslationStringPair,
  VariantTranslationStringMap,
  VariantTranslationStringPair,
} from '@src/app/model/localized-strings';
import { VariantTranslationKey } from '@src/app/generated/globalTypes';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { PermissionName } from '@src/app/model/userdata';
import { MatTableDataSource } from '@angular/material/table';
import { cloneDeep, isEqual } from 'lodash';
import { TaJson } from 'ta-json';
import { first, map } from 'rxjs/operators';
import { VariantTranslations } from '@src/app/model/variant-translations';
import { JsonModal } from '@src/app/components/utils/json-modal';

@Component({
  selector: 'app-translations',
  templateUrl: '../translations/translations.component.html',
  styleUrls: ['../translations/translations.component.scss'],
  standalone: false,
})
export class TranslationsComponent implements OnInit, OnDestroy {
  public appTranslations: LocalizedStringForApp;
  public defaultAppTranslations: LocalizedStringForApp;
  public variantTranslations: LocalizedStringForVariant[] = [];
  public variantTranslationsSrc: MatTableDataSource<VariantTranslations>;
  public variantTranslationsSrcClone: VariantTranslations[] = [];
  public invariantTranslations: LocalizedStringForInvariant;
  public attributeTranslations: LocalizedStringForAttribute;
  public hasEditDefaultPermission$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public buttonsBlocked: boolean = false;
  private _fetching: boolean = false;
  private _componentDestroyed$: Subject<void> = new Subject();
  private _translationFailMsg: string = 'translations.upload.failure';

  public displayedColumns: string[] = ['bpName', 'expandedDetail'];
  public displayedColumns2: string[] = ['varName', 'expandedTranslations'];
  public displayedColumns3: string[] = ['key', 'value'];

  // Indicates if the changed table has differences with the original array
  public unSubmittedChanges: boolean = false;

  constructor(
    private _apolloService: ApolloService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _permissionsService: PermissionsService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {
    timer(0, 1000).pipe(
      takeUntil(this._componentDestroyed$),
      switchMap(_ => this._apolloService.getTranslationUpdatingStatus$())
    ).subscribe((updating) => {
      if (this.buttonsBlocked && !updating && !this._fetching) {
        this._fetching = true;
        this.refreshAll$().then((_) => {
          this.buttonsBlocked = false;
          this._fetching = false;
        });
      } else {
        this.buttonsBlocked = updating;
      }
    });
  }

  public async refreshAll$(): Promise<void> {
    this._apolloService.getVariantTranslationsRefresh();
    await this._apolloService.getTranslationsRefresh(this._hasPermissions());
  }

  public recalculateEqual(): void {
    this.unSubmittedChanges = !isEqual(this.variantTranslationsSrcClone, this.variantTranslationsSrc);
  }

  public ngOnInit(): void {
    this.buttonsBlocked = true;

    this._permissionsService.observePermission$('access_modules', PermissionName.Update)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(this.hasEditDefaultPermission$);

    this._apolloService.getTranslations$()
      .pipe(
        takeUntil(this._componentDestroyed$),
        combineLatestWith(
          this._hasPermissions()
            ? this._apolloService.getDefaultTranslations$()
            : of({ translations: [] })
        )
      )
      .subscribe(([tAll, tDefault]) => {
        this.buttonsBlocked = true;

        const appDefaults = (tDefault.translations as {
          key: string,
          value: string
        }[])?.filter(x => /^app\./.test(x.key));
        const variantDefaults = (tDefault.translations as {
          key: string,
          value: string
        }[])?.filter(x => /^mvar\./.test(x.key));
        const invariantDefaults = (tDefault.translations as {
          key: string,
          value: string
        }[])?.filter(x => /^invariant\./.test(x.key));
        const attributeDefaults = (tDefault.translations as {
          key: string,
          value: string
        }[])?.filter(x => /^attribute\./.test(x.key));

        if (appDefaults?.length > 0) {
          this.defaultAppTranslations = new LocalizedStringForApp(
            new TranslationStringMap(appDefaults.map(y => new TranslationStringPair(y.key, y.value)))
          );
        }
        this._handleSpecificTranslations(tAll, appDefaults, variantDefaults, invariantDefaults, attributeDefaults);
      });

    this.getVariantTranslations();
  }

  private _handleSpecificTranslations(
    translations: { translations: object[] },
    appDefaultTranslations: { key: string, value: string }[],
    variantDefaultTranslations: { key: string, value: string }[],
    invariantDefaultTranslations: { key: string, value: string }[],
    attributeDefaultTranslations: { key: string, value: string }[]
  ) {
    if (translations.translations?.length > 0) {
      const appTranslations = (translations.translations as {
        key: string,
        value: string
      }[]).filter(x => /^app\./.test(x.key));
      const varTranslations = (translations.translations as {
        key: string,
        value: string
      }[]).filter(x => /^mvar\./.test(x.key));
      const invTranslations = (translations.translations as {
        key: string,
        value: string
      }[]).filter(x => /^invariant\./.test(x.key));
      const attrTranslations = (translations.translations as {
        key: string,
        value: string
      }[]).filter(x => /^attribute\./.test(x.key));

      // For the app translations, filter out the values that are the same as the default value for that key
      this.appTranslations = new LocalizedStringForApp(
        this.createTranslationStringMap(appTranslations, appDefaultTranslations)
      );

      // The variant translations are first grouped by groupId
      this.variantTranslations = varTranslations.reduce((prev: LocalizedStringForVariant[], curr) => {
        const patternVariantGroupId = /mvar\.(?<variantGroupId>[^.]+)\.[^.]+/;
        const variantGroupId = patternVariantGroupId.exec(curr.key)[1];

        const existingStringMap = prev.find(m => m.groupId === variantGroupId);
        const defaultDuplicate = variantDefaultTranslations?.find(z => z.key === curr.key);

        const currentKey: VariantTranslationKey = /\.Description/.test(curr.key)
          ? VariantTranslationKey.Description
          : VariantTranslationKey.Title;
        const newPair = defaultDuplicate && curr.value === defaultDuplicate.value
          ? new VariantTranslationStringPair(currentKey, '')
          : new VariantTranslationStringPair(currentKey, curr.value);

        if (existingStringMap) {
          existingStringMap.values.values.push(newPair);
        } else {
          prev.push(new LocalizedStringForVariant(
            variantGroupId,
            new VariantTranslationStringMap([newPair])
          ));
        }

        return prev;
      }, []);

      this.invariantTranslations = new LocalizedStringForInvariant(
        this.createTranslationStringMap(invTranslations, invariantDefaultTranslations)
      );
      this.attributeTranslations = new LocalizedStringForAttribute(
        this.createTranslationStringMap(attrTranslations, attributeDefaultTranslations)
      );
    }
  }

  getVariantTranslations(): void {
    this._apolloService.getVariantTranslations().pipe(
      takeUntil(this._componentDestroyed$),
      map((v) => {
        return new MatTableDataSource<VariantTranslations>(v);
      })).subscribe((r) => {
      this.variantTranslationsSrc = r;
      this.variantTranslationsSrcClone = cloneDeep(this.variantTranslationsSrc.data);
    });
  }

  saveVariantChangesFromTable(): void {
    this.buttonsBlocked = true;

    const myJson = { ti: VariantTranslations.flattenToTranslationJsonFormat(this.variantTranslationsSrc.data) };
    // Filter out the values we substituted an empty string for, as we don't want these in the localized strings
    myJson['ti'] = myJson['ti'].map((x) => {
      x.values.values = x.values.values.filter(t => t.value.trim() !== '');
      return x;
    });
    this._apolloService.setVariantRetailerTranslations$(TaJson.serialize(myJson))
      .pipe(first(), takeUntil(this._componentDestroyed$))
      .subscribe((x) => {
        // set data and refresh everything
        this.variantTranslationsSrcClone = cloneDeep(this.variantTranslationsSrc.data); // set data
        this._apolloService.getVariantTranslationsRefresh();
        this._changeDetectorRef.detectChanges();
        this.recalculateEqual();
      });
  }

  public openEditTranslationsDialog($event: any, type: string, readOnly?: boolean): void {
    $event.stopPropagation();
    let stuffToSend;
    let processFn: (json: JSON) => Observable<boolean>;
    switch (type) {
      case 'app':
        stuffToSend = this.appTranslations;
        processFn = (json) => {
          this.buttonsBlocked = true;
          // Filter out the values we substituted an empty string for, as we don't want these in the localized strings
          json['ti'].values.values = json['ti'].values.values.filter(t => t.value.trim() !== '');
          return this._apolloService.setAppRetailerTranslations$(json);
        };
        break;
      case 'variant':
        stuffToSend = this.variantTranslations;
        processFn = (json) => {
          this.buttonsBlocked = true;
          // Filter out the values we substituted an empty string for, as we don't want these in the localized strings
          json['ti'] = json['ti'].map((x) => {
            x.values.values = x.values.values.filter(t => t.value.trim() !== '');
            return x;
          });
          return this._apolloService.setVariantRetailerTranslations$(json);
        };
        break;
      case 'invariant':
        stuffToSend = this.invariantTranslations;
        processFn = (json) => {
          this.buttonsBlocked = true;
          // Filter out the values we substituted an empty string for, as we don't want these in the localized strings
          json['ti'].values.values = json['ti'].values.values.filter(t => t.value.trim() !== '');
          return this._apolloService.setInvariantRetailerTranslations$(json);
        };
        break;
      case 'attribute':
        stuffToSend = this.attributeTranslations;
        processFn = (json) => {
          this.buttonsBlocked = true;
          // Filter out the values we substituted an empty string for, as we don't want these in the localized strings
          json['ti'].values.values = json['ti'].values.values.filter(t => t.value.trim() !== '');
          return this._apolloService.setAttributeRetailerTranslations$(json);
        };
        break;
      case 'defaultApp':
        this.buttonsBlocked = true;
        stuffToSend = this.defaultAppTranslations;
        processFn = (json) => {
          this.buttonsBlocked = true;
          return this._apolloService.setAppDefaultTranslations$(json);
        };
        break;
    }

    const dialogRef = JsonModal.openJSONModalDialog(this._dialog, new JsonModalDataObject(
      'ti',
      stuffToSend,
      'translations.upload.header',
      'buttons.submit',
      [],
      readOnly || false
    ));

    JsonModal.useJsonDialog(
      dialogRef,
      processFn.bind(this._apolloService),
      this._translateService.instant(this._translationFailMsg),
      () => this._apolloService.getTranslationsRefresh.bind(this._apolloService)(this._hasPermissions())
    );
  }

  public createTranslationStringMap(
    translations: { key: string, value: string }[],
    defaultTranslations: { key: string, value: string }[]
  ): TranslationStringMap {
    return new TranslationStringMap(translations.map((y) => {
      const defaultDuplicate = defaultTranslations?.find(z => z.key === y.key);
      if (defaultDuplicate && y.value === defaultDuplicate.value) {
        return new TranslationStringPair(y.key, '');
      } else {
        return new TranslationStringPair(y.key, y.value);
      }
    }));
  }

  private _hasPermissions(): boolean {
    return this._permissionsService.getPermission('access_all_retailers', PermissionName.Read);
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
